import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, EditIcon, FileTextIcon, HelpCircle } from "lucide-react";
import { useServiceStandardQuery } from "../api/queries";
import {
  ServiceStandardDetailTour,
  useServiceStandardDetailTour,
} from "../tour/ServiceStandardDetailTour";
import { ServiceStandardDetailView } from "../ui/ServiceStandardDetailView";

export function ServiceStandardDetailPage() {
  const navigate = useNavigate({ from: "/app/service-standard/$id" });
  const { id } = useParams({ strict: false });
  const canEdit = useActionPermission(["service_standard:update"]);
  const tour = useServiceStandardDetailTour();

  const {
    data: standard,
    isLoading,
    error,
  } = useServiceStandardQuery(id ?? "");

  const handleEdit = () => {
    navigate({
      to: "/app/service-standard/$id/edit",
      params: { id: id ?? "" },
    });
  };

  const handleBack = () => {
    navigate({ to: "/app/service-standard" });
  };

  const handleViewPDF = () => {
    navigate({
      to: "/app/service-standard/$id/pdf-preview",
      params: { id: id ?? "" },
    });
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
              <p className="mt-2 text-muted-foreground">ກໍາລັງໂຫຼດ...</p>
            </div>
          </div>
        </Main>
      </>
    );
  }

  if (error || !standard) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex min-h-[400px] items-center justify-center">
            <div className="text-center">
              <h2 className="mb-2 font-bold text-2xl text-destructive">
                ບໍ່ພົບມາດຕະຖານບໍລິການ
              </h2>
              <p className="mb-4 text-muted-foreground">
                ມາດຕະຖານບໍລິການທີ່ທ່ານຊອກຫາບໍ່ມີຢູ່ ຫຼື ຖືກລຶບໄປແລ້ວ
              </p>
              <Button onClick={handleBack}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                ກັບໄປທີ່ລາຍການ
              </Button>
            </div>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />

      <Main>
        <ServiceStandardDetailTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Header Actions */}
        <div className="mb-6 flex items-center justify-between" data-tourid="detail-header">
          <Button variant="ghost" onClick={handleBack} className="gap-2">
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບໄປທີ່ລາຍການ
          </Button>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="size-7 rounded-full"
              onClick={tour.startTour}
            >
              <HelpCircle className="size-5" />
            </Button>
            <Button variant="outline" onClick={handleViewPDF} className="gap-2">
              <FileTextIcon className="h-4 w-4" />
              ເບິ່ງ PDF Preview
            </Button>
            {canEdit && (
              <Button onClick={handleEdit} className="gap-2">
                <EditIcon className="h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>

        {/* Service Standard Content */}
        <ServiceStandardDetailView standard={standard} />
      </Main>
    </>
  );
}
